/**
 * Runtime storage orchestrator: health, quota, fallback ordenado.
 */

import type { QueueItem } from '../queue/types';
import type { IQueueStorage, StorageHealth } from './IQueueStorage';
import { AtomicIndexedDBQueueStorage } from './atomicQueueStorage';
import { LocalStorageQueueStorage } from './LocalStorageQueueStorage';
import { MemoryQueueStorage } from './MemoryQueueStorage';
import { getStorageQuota, estimateStorageUsage } from './quotaManager';
import { computeQueueChecksum, validateQueueChecksum } from '../queueIntegrity';
import { recordStorageFallback, recordStorageHealth } from '../telemetry';
import { enterStorageSafeMode, clearStorageSafeMode } from './storageSafeMode';
import {
  pickBestScoredBackend,
  scoreStorageBackend,
} from './storageScoring';
import {
  readGenerationPointer,
  validateGenerationPointer,
} from './generationPointer';

let activeStorage: IQueueStorage | null = null;
let lastKnownGoodChecksum: string | null = null;
let quarantinedGenerations: number[] = [];

async function probeStorageWithScore(
  storage: IQueueStorage,
  corruption: boolean
): Promise<{ storage: IQueueStorage; score: number } | null> {
  const start = Date.now();
  try {
    const health = await storage.health();
    const latencyMs = Date.now() - start;
    const quota = await getStorageQuota();
    const quotaPressure =
      quota.quota && quota.usage
        ? quota.usage / quota.quota
        : 0;
    const score = scoreStorageBackend({
      name: storage.name,
      health,
      latencyMs,
      corruptionDetected: corruption,
      quotaPressure,
    });
    if (!health.ok) return null;
    return { storage, score };
  } catch {
    return null;
  }
}

export async function selectStorageBackend(): Promise<IQueueStorage> {
  const candidates = [
    new AtomicIndexedDBQueueStorage(),
    new LocalStorageQueueStorage(),
    new MemoryQueueStorage(),
  ];
  const scored: Array<{ storage: IQueueStorage; score: number }> = [];
  for (const storage of candidates) {
    const result = await probeStorageWithScore(storage, false);
    if (result) scored.push(result);
  }
  const best = pickBestScoredBackend(scored);
  if (best) {
    recordStorageHealth(best.storage.name, true);
    return best.storage;
  }
  recordStorageFallback();
  recordStorageHealth('memory', true);
  return new MemoryQueueStorage();
}

export function getQuarantinedGenerations(): readonly number[] {
  return quarantinedGenerations;
}

export function quarantineGeneration(generation: number): void {
  if (!quarantinedGenerations.includes(generation)) {
    quarantinedGenerations.push(generation);
  }
}

export async function getOrchestratedStorage(): Promise<IQueueStorage> {
  if (!activeStorage) {
    activeStorage = await selectStorageBackend();
  }
  return activeStorage;
}

export async function loadQueueOrchestrated(): Promise<QueueItem[]> {
  const storage = await getOrchestratedStorage();
  const items = await storage.loadAll();
  void estimateStorageUsage(items);
  const meta = await readGenerationPointer();
  if (
    meta &&
    quarantinedGenerations.includes(meta.activeGeneration)
  ) {
    recordStorageHealth(storage.name, false);
    return [];
  }
  if (!validateGenerationPointer(items, meta)) {
    recordStorageHealth(storage.name, false);
    enterStorageSafeMode('generation_mismatch');
    if (meta) quarantineGeneration(meta.activeGeneration);
    const ls = new LocalStorageQueueStorage();
    try {
      const backup = await ls.loadAll();
      if (validateGenerationPointer(backup, null) || backup.length > 0) {
        return backup;
      }
    } catch {
      // ignore
    }
    return [];
  }
  const checksum = computeQueueChecksum(items);
  if (
    lastKnownGoodChecksum &&
    items.length > 0 &&
    !validateQueueChecksum(items, lastKnownGoodChecksum)
  ) {
    recordStorageHealth(storage.name, false);
    return items;
  }
  lastKnownGoodChecksum = checksum;
  clearStorageSafeMode();
  return items;
}

export async function saveQueueOrchestrated(items: QueueItem[]): Promise<void> {
  const storage = await getOrchestratedStorage();
  await storage.saveAll(items);
  void estimateStorageUsage(items);
  lastKnownGoodChecksum = computeQueueChecksum(items);
  if (storage.name === 'indexeddb') {
    try {
      await new LocalStorageQueueStorage().saveAll(items);
    } catch {
      // backup optional
    }
  }
}

export async function getOrchestratedStorageHealth(): Promise<StorageHealth & { quota?: { usage?: number; quota?: number } }> {
  const storage = await getOrchestratedStorage();
  const health = await storage.health();
  const quota = await getStorageQuota();
  return { ...health, quota };
}

export function _testResetStorageOrchestrator(): void {
  activeStorage = null;
  lastKnownGoodChecksum = null;
}
